import type { DimensionId, PersonalityAnalysis, PlanDefinition, Question, ResultCode, ResultDefinition, WorkhorseIdentity } from "../types";

export const questions: Question[] = [
  {
    id: "Q1",
    dimension: "D1",
    kicker: "周一开机",
    prompt: "周一早晨闹钟响时，你的第一反应是？",
    options: [
      { id: "Q1_A", label: "垂死病中惊坐起，牛马竟是我和你！", score: 3, tags: ["EXTRA_GIVE"] },
      { id: "Q1_B", label: "按掉再睡十分钟，向国家申请下午上班加入劳动法", score: 1, tags: ["SLEEP_DEBT"] },
      { id: "Q1_C", label: "像个没有感情的开机键，10 分钟洗漱上班", score: 2, tags: ["LOW_EMOTION"] },
    ],
  },
  {
    id: "Q2",
    dimension: "D2",
    kicker: "画饼反应",
    prompt: "面对老板画的“大饼”，你的内心 OS 是？",
    options: [
      { id: "Q2_A", label: "OK OK OK，福利给到位，你说的都对", score: 2, tags: ["LOW_EMOTION"] },
      { id: "Q2_B", label: "这饼太油了，我不吃，我要减肥", score: 3, tags: ["BOUNDARY_CLEAR"] },
      { id: "Q2_C", label: "老板给加餐了，这是什么成分的饼？", score: 1 },
    ],
  },
  {
    id: "Q3",
    dimension: "D4",
    kicker: "单休回血",
    prompt: "周末只有 1 天休息（或加班后），你会怎么度过？",
    options: [
      { id: "Q3_A", label: "报复性熬夜，刷剧打游戏，绝不早睡", score: 3, tags: ["SLEEP_DEBT"] },
      { id: "Q3_B", label: "躺平 24 小时，除了拿外卖绝不下床", score: 2, tags: ["REST_DEBT"] },
      { id: "Q3_C", label: "已经在看招聘软件，或者在整理交接文档了", score: 1, tags: ["EXIT_INTENT"] },
    ],
  },
  {
    id: "Q4",
    dimension: "D2",
    kicker: "办公室社交",
    prompt: "关于办公室社交，你的状态是？",
    options: [
      { id: "Q4_A", label: "莫挨老子，我是来打工的，不是来八卦的", score: 3, tags: ["LOW_EMOTION"] },
      { id: "Q4_B", label: "来加入大家庭，自备键盘办公椅，还自备零食投喂同事", score: 1, tags: ["EXTRA_GIVE"] },
      { id: "Q4_C", label: "八卦只是饭后甜点，中午吃什么才是终极问题", score: 2, tags: ["FOOD_REWARD"] },
    ],
  },
  {
    id: "Q5",
    dimension: "D3",
    kicker: "工位搭子",
    prompt: "你的颈椎和腰椎现在感觉如何？",
    options: [
      { id: "Q5_A", label: "它是我的“终身打工搭子”，低头敲键盘时它最忠诚", score: 2, tags: ["DESK_STRAIN"] },
      { id: "Q5_B", label: "问就是麻木了，靠 9.9 的咖啡和钢铁般意志力撑着", score: 1, tags: ["DESK_STRAIN", "CAFFEINE_SUPPORT"] },
      { id: "Q5_C", label: "“老子不肝了！”发自灵魂的呐喊，小命重要", score: 3, tags: ["BOUNDARY_CLEAR"] },
    ],
  },
];

export const dimensions: Record<DimensionId, { name: string; hint: string }> = {
  D1: { name: "工作投入度", hint: "你对任务启动、推进和结果负责的程度" },
  D2: { name: "边界保护度", hint: "面对画饼和办公室社交时的边界强度" },
  D3: { name: "精力恢复度", hint: "短休、睡眠和周末之后的回血效率" },
  D4: { name: "压力补偿度", hint: "依赖奶茶、外卖、熬夜等补偿压力的程度" },
};

export const behaviorLabels: Record<string, string> = {
  EXTRA_GIVE: "习惯主动兜底",
  LOW_EMOTION: "低情绪劳动",
  SLEEP_DEBT: "睡眠欠账",
  DESK_STRAIN: "久坐紧绷",
  FOOD_REWARD: "食物奖励型",
  EXIT_INTENT: "离场行动派",
  BOUNDARY_CLEAR: "边界清醒",
  REST_DEBT: "补觉式回血",
  CAFFEINE_SUPPORT: "咖啡续命",
};

const resultData: Record<ResultCode, Omit<ResultDefinition, "code">> = {
  COOL: {
    name: "无糖牛马",
    headline: "该交付交付，该热闹免谈。",
    personality: "你不是没有职场热情，而是很少把热情浪费在没有边界的地方。面对清晰目标，你可以专注、可靠地交付；面对画饼、情绪绑架和无效社交，你会本能地后退一步。你对同事关系有筛选，也习惯把情绪留在工作之外，因此偶尔会被误解为“冷”。真正的你更像一套自带防火墙的工作系统：该承担的会完成，不该吞下的不会轻易接住。",
    shareText: "我的班味配方：能力在线，糖分归零。",
    banweiLabel: "10%",
    image: "/assets/results/cool-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你冷静可靠，不把热情浪费在无效关系和模糊规则上。",
      work: "目标清晰时专注交付，遇到画饼和无效协作会主动降噪。",
      boundary: "同事是合作伙伴，不靠讨好换安全感，也不轻易接情绪债。",
      recovery: "表面平静不等于电量无限，你仍需要规律活动和真正下线。",
    },
    poster: { image: "/assets/results/cool-lowpoly.webp", accent: "#4f7047", soft: "#e8f0e4", foreground: "#ffffff" },
  },
  GIVE: {
    name: "倒贴牛马",
    headline: "事情还没落到你头上，你已经先说“我来吧”。",
    personality: "你很容易成为团队里那个“先接住再说”的人。别人遇到难题，你会补位；现场需要有人收尾，你会留下；关系出现尴尬，你也常主动缓和。你的可靠并不完全来自工作狂属性，而更多来自对关系和责任的敏感。问题是，当帮助没有边界，你的善意就会被默认为免费资源。你真正需要的不是变得冷漠，而是在答应之前先确认责任、成本和回报。",
    shareText: "别人上班领工资，我上班顺便给公司充值。",
    banweiLabel: "50%",
    image: "/assets/results/give-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你总能先看见别人的难处，也习惯成为团队最后的兜底人。",
      work: "任务牵涉具体的人时投入最高，救场协调往往比本职更多。",
      boundary: "常常先答应再算成本，善意很容易被默认为免费资源。",
      recovery: "白天维持可靠，疲惫却在独处后集中出现，需要提前停手。",
    },
    poster: { image: "/assets/results/give-lowpoly.webp", accent: "#93482f", soft: "#f5e5dc", foreground: "#ffffff" },
  },
  ONER: {
    name: "单休战马",
    headline: "工作日全速拉磨，休息日报复性夺回人生。",
    personality: "你像一匹听见发令枪就会冲出去的战马：任务越明确、节点越紧，你越能把速度和执行力拉满。可高投入的成本不会消失，它常在夜晚变成报复性熬夜、刷手机、吃喝或彻底躺平。你并非不会休息，而是习惯把休息推迟到“全部做完以后”。你的优势是强启动和强执行；真正需要升级的，是让恢复进入日常节奏，而不是等身体替你强制停机。",
    shareText: "我的周末只有一天，但我的工位有六天。",
    banweiLabel: "110%",
    image: "/assets/results/oner-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你是高启动高消耗型选手，越忙越容易进入战斗状态。",
      work: "明确期限和短期战役最能激发你，通常选择先做完再说。",
      boundary: "为了进度和评价会临时加码，拒绝能力随压力上下波动。",
      recovery: "休息总被推到通关之后，夜晚和周末容易报复性回血。",
    },
    poster: { image: "/assets/results/oner-lowpoly.webp", accent: "#675083", soft: "#ede8f3", foreground: "#ffffff" },
  },
  NITE: {
    name: "大黑马",
    headline: "白天靠惯性开机，夜里才把时间还给自己。",
    personality: "你不一定是真正的夜班人，更像是把自主权推迟到夜里的人。白天的你可以维持运转，却容易在低意义、低反馈的工作中进入自动驾驶；到了晚上，才觉得时间终于属于自己，于是刷屏、熬夜或吃点东西，把被占用的一天补回来。你并不缺边界意识，真正缺的是白天可以自己支配的空间。你的核心课题不是简单地早点睡，而是减少白天的失控感。",
    shareText: "月亮不睡我不睡，白天工位打瞌睡。",
    banweiLabel: "0%",
    image: "/assets/results/nite-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "白天靠惯性运转，夜晚才觉得时间终于真正属于自己。",
      work: "重复和低反馈任务容易自动驾驶，自主节奏才能让你提速。",
      boundary: "你知道自己不想要什么，却仍被白天规则占满心理空间。",
      recovery: "刷屏熬夜是在补偿失控感，却也透支了第二天的启动电量。",
    },
    poster: { image: "/assets/results/nite-lowpoly.webp", accent: "#253e66", soft: "#e7ebf2", foreground: "#ffffff" },
  },
  HARD: {
    name: "OK 马",
    headline: "嘴上还能说 OK，身体已经开始掉线。",
    personality: "你最典型的状态，是嘴上仍能回答“OK”，身体却早已开始掉线。你不一定一直拼命，有时投入下降本身就是电量不足的结果；但只要任务还能推进，你就容易把僵硬、困倦、疼痛和注意力涣散解释成“小问题”。你的边界未必差，真正薄弱的是对身体信号的响应速度。你需要的不是再证明一次自己能扛，而是把短休、活动和睡眠放到崩溃之前。",
    shareText: "嘴上 OK，工位电量只剩 8%。",
    banweiLabel: "夯中夯",
    image: "/assets/results/hard-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "嘴上还能回答OK，身体却早已用困倦和疼痛发出警报。",
      work: "只要任务还能推进，你就会把身体掉线解释成小问题。",
      boundary: "能拒绝额外任务，却很少允许自己从现有任务真正退出。",
      recovery: "常等彻底没电才一次性休息，需要把短休放到崩溃之前。",
    },
    poster: { image: "/assets/results/hard-lowpoly.webp", accent: "#416f56", soft: "#e6f0e9", foreground: "#ffffff" },
  },
  LEAN: {
    name: "脱脂牛马",
    headline: "不是不努力，是不再拿身体替工作买单。",
    personality: "你不是所谓“爱运动、会控脂”的生活方式标签，而是一个边界和恢复意识都很清楚的人。任务值得时，你可以投入；成本失衡时，你也愿意及时减量、协商或停下。你不太接受拿睡眠、健康和私人生活为组织目标长期买单，因此有时会被误读为“不够拼”。其实你的底层逻辑很务实：真正可靠的产出应该可以持续，而不是靠一次次燃烧自己换来。",
    shareText: "班味有一点，生活还是原味。",
    banweiLabel: "42%",
    image: "/assets/results/lean-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你把可持续放在首位，不愿用健康证明自己足够努力。",
      work: "值得的任务认真推进，不合理的消耗会被你及时降权。",
      boundary: "底线清楚，能协商节奏，也不接受长期用私人生活买单。",
      recovery: "你会主动休息和调整，而不是等透支后再报复性补偿。",
    },
    poster: { image: "/assets/results/lean-lowpoly.webp", accent: "#447965", soft: "#e5efeb", foreground: "#ffffff" },
  },
  FULL: {
    name: "全脂牛马",
    headline: "白天把自己分给所有人，晚上再用快乐补偿回来。",
    personality: "你并不是被体重或食量定义的人。你的共同特征，是白天把大量精力分给别人：回应需求、照顾气氛、替人补位、尽量不让关系难看。因为边界偏低，这些看不见的付出会挤压真正用于工作的电量，也让你在下班后更想用吃喝、刷屏或熬夜奖励自己。你需要解决的不是“管住嘴”，而是别再把自己当成团队的无限续杯。",
    shareText: "我的班味全脂加糖，快乐先续上再说。",
    banweiLabel: "96%",
    image: "/assets/results/full-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你习惯把精力分给所有人，自己的需求总被安排到最后。",
      work: "大量能量花在回应、协调和补位，承担着隐形关系劳动。",
      boundary: "害怕冲突让你容易接受额外安排，团队需要总排在自己前。",
      recovery: "白天越缺少被照顾的感觉，晚上越想用即时快乐补回来。",
    },
    poster: { image: "/assets/results/full-lowpoly.webp", accent: "#76520f", soft: "#f2e8ce", foreground: "#ffffff" },
  },
  QUIT: {
    name: "临期牛马",
    headline: "你不是统一的一种人，只是和这份工作的关系已经进入退出模式。",
    personality: "“临期牛马”不是一套统一性格，而是一种职业关系状态。只有当离场行动与明确的自我保护信号同时出现，算法才会优先识别这个结果；单独一次查看招聘软件，不会覆盖其余四题形成的角色。你可能仍在认真交付，也可能已经心理抽离；可能果断，也可能因为现实成本反复犹豫。这个结果不替你下决定，只提醒你追问：我想离开的究竟是任务、团队、管理方式，还是整个方向？",
    shareText: "这匹牛马已进入交接倒计时。",
    banweiLabel: "0.01%",
    image: "/assets/results/quit-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你不是固定人格，而是和这份工作的关系进入退出阶段。",
      work: "可能仍在专业交付，也可能已经抽离，留下意愿正在下降。",
      boundary: "决定与顾虑同时存在，团队评价和现实成本仍牵动选择。",
      recovery: "先弄清想离开的是任务、团队还是方向，再决定下一步。",
    },
    poster: { image: "/assets/results/quit-lowpoly.webp", accent: "#8e473e", soft: "#f3e3df", foreground: "#ffffff" },
  },
  MIXD: {
    name: "混血牛马",
    headline: "启动慢，但底线清；不爱硬撑，也不愿被随便归类。",
    personality: "你不是随机拼出来的“混合款”，而是一组很稳定但没被旧模板准确接住的人：启动偏慢，恢复和自保意识却很强。你很少因为一句口号就把能量全部交出去，更愿意判断任务是否值得、回报是否匹配、身体是否承受得住。这样的你容易被误读为懒散，其实很多时候只是缺少足以启动的意义和反馈。你的优势是不过度燃烧；需要补上的，是把目标拆成更小的第一步。",
    shareText: "我的牛马成分过于复杂，系统建议单独立项。",
    banweiLabel: "72%",
    image: "/assets/results/mixd-lowpoly.webp",
    accent: "#e0f0d8",
    highlights: {
      core: "你不是随机混合，而是低启动高自保的非模板型角色。",
      work: "需要意义、兴趣和清晰反馈启动，力气只投向认可的事情。",
      boundary: "面对明显不合理的要求不会照单全收，底线处于中高区间。",
      recovery: "恢复意识稳定，也懂优先保护身体，但仍会寻找即时回报。",
    },
    poster: { image: "/assets/results/mixd-lowpoly.webp", accent: "#606a45", soft: "#ebede3", foreground: "#ffffff" },
  },
};

export const results = Object.fromEntries(
  Object.entries(resultData).map(([code, value]) => [code, { code, ...value }]),
) as Record<ResultCode, ResultDefinition>;

export const workhorseIdentities: Record<ResultCode, WorkhorseIdentity> = {
  COOL: {
    name: "无糖牛马",
    employeeId: "ZERO-SUGAR-010",
    ingredients: "80%的冷静 + 20%的职业礼貌 + 0%的无效糖分",
    tags: ["已开启降噪", "拒绝画饼", "情绪免打扰", "准点下线"],
    slogan: "工作可以加急，情绪价值请走采购流程。",
  },
  GIVE: {
    name: "倒贴牛马",
    employeeId: "PREPAID-888",
    ingredients: "10%的本职 + 90%的额外服务 + 无限续杯的善意",
    tags: ["免费续杯", "随叫随到", "团队售后", "拒绝困难"],
    slogan: "工资到账一次，善意自动续费一整月。",
  },
  ONER: {
    name: "单休战马",
    employeeId: "ONE-DAY-OFF-996",
    ingredients: "70%的冲锋 + 29%的硬撑 + 1%的周末余额",
    tags: ["冲刺型选手", "周末欠费", "先做完再说", "休息候补"],
    slogan: "只要终点够近，我可以暂时忘记自己有腿。",
  },
  NITE: {
    name: "大黑马",
    employeeId: "NIGHT-OWL-003",
    ingredients: "20%的白天待机 + 80%的深夜满血 + 一轮明月",
    tags: ["夜间管理员", "白天屏保", "熬夜赎身", "月光工位"],
    slogan: "白天属于公司，凌晨两点终于轮到我本人。",
  },
  HARD: {
    name: "OK 马",
    employeeId: "STILL-OK-008",
    ingredients: "8%的电量 + 92%的嘴硬 + 十七条身体警告",
    tags: ["嘴上OK", "身体掉线", "稍后提醒", "咖啡外接"],
    slogan: "我还能坚持，前提是别问我坚持的是什么。",
  },
  LEAN: {
    name: "脱脂牛马",
    employeeId: "LOW-FAT-042",
    ingredients: "60%的边界 + 40%的认真 + 0%的自我燃烧",
    tags: ["低耗运行", "拒绝内卷", "成本清醒", "生活原味"],
    slogan: "班可以上，命只有一条，请按项目预算使用。",
  },
  FULL: {
    name: "全脂牛马",
    employeeId: "FULL-FAT-096",
    ingredients: "50%的热心 + 46%的情绪劳动 + 4%的自己",
    tags: ["气氛组长", "人际售后", "无限续杯", "快乐补偿"],
    slogan: "白天照顾全世界，晚上再点一份自己。",
  },
  QUIT: {
    name: "临期牛马",
    employeeId: "EXPIRED-SOON-404",
    ingredients: "10%的演技 + 90%的离职倒计时 + 0%的职场内耗",
    tags: ["已读不回", "坐等跑路", "心态超然", "准自由人"],
    slogan: "世界那么大，老牛我想去看看（并在下周提离职）。",
  },
  MIXD: {
    name: "混血牛马",
    employeeId: "HYBRID-072",
    ingredients: "33%的观望 + 33%的清醒 + 34%的随机高性能",
    tags: ["拒绝归类", "按需启动", "隐藏性能", "单独立项"],
    slogan: "本牛马成分复杂，建议不要轻易复制粘贴。",
  },
};

export const personalityAnalyses: Record<ResultCode, PersonalityAnalysis> = {
  COOL: {
    core: "你是职场零糖款：能力在线，情绪添加剂为零。该交付交付，该热闹自动过滤。",
    work: "目标清楚就满速运行，需求飘忽就切省电。你不是不能卷，只是不替空气赛跑。",
    boundary: "公事刷卡进入，情绪债请先预约。同事能做搭档，但共享群聊不会自动升级成家人。",
    full: "恭喜，你的职场系统自带高级垃圾邮件过滤器：明确任务正常接收，画饼、绑架和强行团建自动进回收站。你愿意认真做事，却拒绝参加“谁看起来更爱公司”的行为艺术；你希望被看见的是交付，不是午餐坐哪桌。需要留意的是，防火墙太高也会把正常协作拦掉，必要的同步和求助不是边界失守。你适合权责清楚、少开无效会、评价不靠读心术的团队。再稳定的系统也要关机：离屏、活动和真正下班不是性能故障，而是例行维护。",
  },
  GIVE: {
    core: "你是公司隐藏付费会员：工资按月到账，责任全年自动续费。“我来吧”总比理智先加载。",
    work: "人形应急服务台，救场、协调、收尾一条龙。哪里冒烟，你先拎灭火器。",
    boundary: "善意长期免密支付：先答应，后看余额，最后发现时间、精力和午休一起被扣完。",
    full: "恭喜，你解锁了团队最舍不得失去、也最容易忘记心疼的角色。别人遇到麻烦，你补位；责任人消失，你收尾；空气突然安静，你甚至想顺手负责气氛。你的可靠来自对关系和责任的敏感，但救火次数多了，大家可能忘记先检查是谁在玩火。真正要升级的不是善良，而是给善良加上额度和确认按钮。每次说“我来吧”前先问：谁负责、何时交、我接了要放下什么。你适合贡献有记录、救场有补偿、拒绝不会被记仇的团队，而不是用一句“辛苦了”结算所有加班。",
  },
  ONER: {
    core: "你是工位赛道上的战马：发令枪一响，别人还在找文档，你已经冲到第二版。",
    work: "先冲线，再研究自己为什么在流血。短周期越紧，你的执行力越像开了氮气。",
    boundary: "平时会说不，项目一红就自动打折。嘴上“最后一次”，手里已接过下一项加急。",
    full: "恭喜，你拥有把截止日期当兴奋剂的稀有体质。任务越急、节点越近、群里头像亮得越整齐，你越能进入战斗状态；速度让你获得掌控感，也让组织误把爆发当成日常产能。你总想等全部做完再休息，可工作这款游戏没有通关动画，于是唯一的休息日被压缩成熬夜、刷屏、吃喝和躺平组成的回血包。白天透支自由，夜晚再透支明天，像拿两张信用卡互相还款。你适合冲刺有期限、结束真能收兵的团队。喝水、走动、准点吃饭不是掉队，而是别让低电量先把你拉下赛道。",
  },
  NITE: {
    core: "你是昼伏夜出的工位大黑马：白天像屏保，夜里获得管理员权限。现在才轮到你活。",
    work: "无聊任务让你自动驾驶，有兴趣和自主权就从 2G 切光纤。启动键只认“为什么”。",
    boundary: "画饼你连反式脂肪酸都看得出，却没守住“我的时间也算时间”。白天仍被安排塞满。",
    full: "恭喜，你不是被月亮选中，而是终于等到所有人停止给你发消息。白天的重复任务让手在操作、灵魂在加载；夜里没有待办追赶，你才敢把生活从后台切回前台。熬夜、刷屏、夜宵和咖啡因像一群热心但不靠谱的朋友：当下帮你找回自由，第二天集体失联，只留下更难启动的早晨。你争的其实不是凌晨两点，而是一段不被安排的自主时间。适合你的环境应当节奏可预测、少临时打断、允许弹性安排。先在白天留出真正属于自己的小块时间，比单纯命令自己早睡更可能结束这笔高利贷。",
  },
  HARD: {
    core: "你是人体系统里的“忽略全部警告”：嘴上持续 OK，身体已经弹出十七个错误窗口。",
    work: "只要手还能碰键盘，就算满电运行。任务过了终点，身体更新继续点“稍后提醒”。",
    boundary: "能拒绝新活，却不肯放下旧活。接下的任务仿佛获得永久居留权。",
    full: "恭喜，你把“还能动”发展成了一套严谨的健康评估体系。肩颈报警、眼睛转圈、注意力蓝屏，你仍能用一句“问题不大”完成故障屏蔽。你不是感觉不到疲惫，而是习惯把身体信号排到任务之后，直到它们集体升级成红色告警。你对别人未必没边界，真正难的是对当前任务说“今天到这里”。咖啡和躺平刷屏像应急电源，能让屏幕亮一会儿，却不能修复线路。你适合允许短休、工位可调整、任务能拆分且不奖励硬撑的环境。规律活动、离屏和睡眠应在彻底死机前安排。",
  },
  LEAN: {
    core: "你是牛马界的成本会计：努力可以，拿健康和生活当免费耗材不行。",
    work: "值得的事认真推进，回报失衡就及时降级。你信长期交付，不信熬夜奇迹。",
    boundary: "边界像配料表一样清楚：协作可以有，情绪绑架不在本产品成分内。",
    full: "恭喜，你的大脑会为每个需求自动生成《对睡眠、健康与私人生活影响评估》。你不是拒绝努力，而是拒绝把自己作为免费耗材写进项目预算。清晰目标、有限承诺和可持续方法最能发挥你的实力；连续熬夜创造的“奇迹”，在你看来多半只是忘了统计返工。你可能因此被误读为不够拼，其实你做的是职场风险管理。你适合尊重个人节律、看长期产出、允许协商优先级的团队。需要补的一课是把取舍依据说清楚：边界不会自动附带说明书，合理止损也需要沟通，才能避免被误会成消极。",
  },
  FULL: {
    core: "你是职场情绪自助餐的全脂款：白天把自己分给所有人，晚上才发现没给自己留一份。",
    work: "本职之外还兼职气氛组、润滑油和人际售后。大家享受得自然，统计得随缘。",
    boundary: "边界长期“无限续杯”：别人一开口你先满足，自己的不舒服永远排在下个迭代。",
    full: "恭喜，你的付出从任务到情绪一项都没脱脂。安抚焦虑、接住冷场、替人补位、帮健忘的同事记截止时间，这些劳动不在看板上，却真实消耗电量。因为太能接，别人容易把你的体谅当默认配置；你则在下班后用吃喝、刷屏、购物或熬夜给自己发一笔迟到的奖金。问题不在那杯奶茶，而在你总要辛苦到极限，才觉得自己配得上快乐。你适合职责清楚、有人分担关系劳动、拒绝不会被记仇的团队。先给帮助他人设置预算，别一边给全公司续杯，一边让自己的杯子长期见底。",
  },
  QUIT: {
    core: "你的肉身仍在打卡，灵魂已开始整理桌面文件。不是摆烂，是职业关系进入保质期倒计时。",
    work: "可能仍在体面交付，也可能把新需求都听成“交接材料新增一项”。心理账户不想再充值。",
    boundary: "离场按钮连着房租、合同和下一份工作的加载进度。犹豫不是软弱，是成年人接口太多。",
    full: "系统检测到：你和这份工作的关系正在进入退出模式。临期不是固定性格，你原本可能敬业、热心、能扛或边界清楚，只是留下来的理由正在逐个下线。想走可能来自过劳、成长停滞、价值冲突、人际消耗或新的机会；若不分原因只靠请假回血，就像给即将搬家的房间换灯。先确认你想离开的是任务、团队、管理方式还是整个方向，再判断内部调整、休息或外部机会哪种更合适。测试可以替你递交情绪辞职信，却不能签正式文件；财务安全垫、合同、社保、机会真实性和交接安排，都要在转身前核对。",
  },
  MIXD: {
    core: "你成功让分类算法申请加班：九宫格装不下，只好单独立项。不是随机，是把力气花得挑剔。",
    work: "启动键不收口号，只认意义、兴趣、反馈或截止时间。任务值得时，隐藏性能突然上线。",
    boundary: "明显不合理的要求很难让你买单。你不一定拍桌，但脑内投入产出表已经算完。",
    full: "恭喜，你是一款低启动、高自保、拒绝被模板批量生产的混血型号。无聊又没反馈的任务，会让你的效率像会议里无人回应的提问一样安静；事情一旦有意义、有兴趣或快到截止时间，隐藏性能又能突然上线。你不是完全躺平，只是经常觉得连打开文件都不符合投入回报比。自保让你不容易无效燃烧，却不会自动生成前进动力。你适合目标清楚、方法自主、反馈及时的环境。给重要事项设置小到不好意思拒绝的第一步，别等兴趣、状态和完美时机同时到岗——那三位同事很可能永远在路上。",
  },
};

export const stateQuestions = [
  {
    id: "S01",
    prompt: "你现在最想先改善什么？",
    options: [
      ["ENERGY", "下午没精神"],
      ["SLEEP", "睡眠不足"],
      ["NECK", "颈肩腰背不适"],
      ["FOOD", "饮食不规律"],
      ["MOOD", "烦躁内耗"],
    ],
  },
  {
    id: "S02",
    prompt: "你每天愿意投入多少时间？",
    options: [["5", "5 分钟"], ["10", "10 分钟"], ["15", "15 分钟"]],
  },
] as const;

export const plans: Record<string, PlanDefinition> = {
  ENERGY: {
    code: "ENERGY",
    name: "午后电量管理",
    variants: {
      "5": {
        headline: "先把下午三点的掉线，拉回来一点点",
        reason: "午后低电量通常与补水不足、午餐节律以及长时间连续工作有关。你不需要立刻彻底改变作息，先用 5 分钟完成一次快速切换。",
        actions: ["喝一杯常温水，暂时不靠奶茶或高糖饮料提神", "离开座位活动 2 分钟，走到窗边或稍远的位置再回来", "给当前最难的任务设定一个 3 分钟开头，只完成第一小步"],
        notice: "如果疲惫长期持续、休息后仍无法缓解或明显影响生活，请及时寻求专业帮助。",
      },
      "10": {
        headline: "给午后安排一次 10 分钟电量重启",
        reason: "长时间坐在工位上，大脑容易进入低效运转。短暂补水、活动和任务切换，通常比继续硬撑更容易恢复状态。",
        actions: ["用 2 分钟补水，并暂时放下手机和工作消息", "用 3 分钟缓慢走动，活动肩颈和腿部", "用 5 分钟处理一个明确的小任务，重新建立完成感"],
        notice: "不要在明显疲惫时持续叠加咖啡因，避免影响晚间睡眠。",
      },
      "15": {
        headline: "用 15 分钟重建你的午后工作节奏",
        reason: "如果下午经常长时间没精神，可能不只是缺少一次提神。用一刻钟依次完成活动、休息和任务启动，更容易重新进入工作状态。",
        actions: ["午饭后慢走 5 分钟，让身体离开久坐状态", "找安静位置闭眼休息 5 分钟，不刷短视频，不处理消息", "回到工位后用 5 分钟完成最重要任务的第一小步，不同时开启多个任务"],
        notice: "若长期伴随头晕、心悸、极度困倦或其他明显不适，建议咨询专业人员。",
      },
    },
  },
  SLEEP: {
    code: "SLEEP",
    name: "睡眠补账",
    variants: {
      "5": {
        headline: "今晚先提前 5 分钟结束这一天",
        reason: "睡眠改善不一定要从“立刻早睡一小时”开始。先建立固定的结束信号，比给自己制定过高目标更容易坚持。",
        actions: ["比平时提前 5 分钟停止刷短视频和工作消息", "把手机放到伸手拿不到的位置，并开启勿扰模式", "躺下后缓慢呼吸，不再临时处理新的事情"],
        notice: "长期失眠、早醒、日间嗜睡或情绪明显受影响时，建议咨询专业人员。",
      },
      "10": {
        headline: "给大脑一个 10 分钟下线过程",
        reason: "工作和信息刺激会让大脑保持在持续响应状态。睡前增加一个短暂缓冲，比直接从刷手机切换到睡觉更自然。",
        actions: ["用 3 分钟处理洗漱、调暗灯光并关闭工作提醒", "用 3 分钟写下明天最重要的三件事，停止在脑中反复记忆", "用 4 分钟进行缓慢呼吸或安静躺卧，不再接触屏幕"],
        notice: "不要因为一两天没有立刻见效就延长熬夜，重点是形成稳定的睡前信号。",
      },
      "15": {
        headline: "建立一套 15 分钟睡前下线仪式",
        reason: "睡眠问题往往与身体疲惫但大脑没有停止工作有关。一套短而固定的睡前流程，可以逐渐帮助身体识别休息时间。",
        actions: ["用 5 分钟完成洗漱、调暗灯光并关闭工作提醒", "用 5 分钟整理第二天用品，写下明天最重要的三件事", "用 5 分钟轻松阅读、缓慢呼吸或安静躺卧，不再接触屏幕"],
        notice: "保持相对固定的起床时间通常比周末一次性睡很久更有助于稳定节律。",
      },
    },
  },
  NECK: {
    code: "NECK",
    name: "久坐舒展",
    variants: {
      "5": {
        headline: "不用大练，先让身体离开椅子 5 分钟",
        reason: "长时间固定姿势会让颈肩腰背持续紧张。短暂改变姿势并规律活动，通常比偶尔进行一次高强度拉伸更容易坚持。",
        actions: ["起身走动 2 分钟，让腿部和腰背离开固定坐姿", "双肩缓慢向后绕环 6 次，动作轻柔，不追求幅度", "调整屏幕、座椅和键盘位置，避免继续低头或耸肩工作"],
        notice: "动作过程中出现明显疼痛、麻木、眩晕或放射性不适时，应停止并及时就医。",
      },
      "10": {
        headline: "用 10 分钟解除一轮工位僵硬",
        reason: "久坐带来的不适通常不只集中在颈部，肩背、髋部和腿部也可能持续紧张，需要用走动和舒展组合恢复。",
        actions: ["用 3 分钟缓慢走动，让身体从久坐状态中退出", "用 4 分钟轻柔活动肩颈、胸背和髋部，不快速扭转", "用 3 分钟重新调整显示器高度、坐姿和桌面距离"],
        notice: "舒展以轻松为准，不要强行压颈、甩头或追求关节发出响声。",
      },
      "15": {
        headline: "给久坐身体安排一次完整的 15 分钟恢复",
        reason: "如果颈肩腰背经常不适，只进行几次肩颈活动可能不够。用一刻钟结合走动、舒展和工位调整，会更完整。",
        actions: ["用 5 分钟散步，让全身恢复活动和自然摆动", "用 5 分钟轻柔活动肩颈、胸背、髋部和腿部", "用 5 分钟调整桌椅、屏幕和常用物品位置，并设置每 45～60 分钟起身提醒"],
        notice: "持续疼痛、麻木、无力或活动受限时，不要自行反复拉伸，应尽快就医。",
      },
    },
  },
  FOOD: {
    code: "FOOD",
    name: "饮食节律",
    variants: {
      "5": {
        headline: "不追求完美，先为下一顿留一个选择",
        reason: "压力大时，人更容易跳过正餐，再通过高糖、高油食物补偿。先准备一个简单可执行的备选方案，比要求自己突然严格控制饮食更有效。",
        actions: ["用 1 分钟确定下一顿正餐的大致时间", "准备一杯水和一份方便获取的简单加餐", "想点外卖时先判断自己是饿、累、烦，还是只是想获得奖励"],
        notice: "这是生活节律建议，不代替营养建议或疾病诊断。",
      },
      "10": {
        headline: "用 10 分钟为今天的饮食减少一次失控",
        reason: "饮食失控经常发生在没有准备、过度饥饿或情绪疲惫的时候。提前做一个简单决定，可以减少临时选择带来的压力。",
        actions: ["用 3 分钟确定今天下一顿正餐的时间和基本搭配", "用 4 分钟准备水、水果、牛奶或其他方便加餐", "用 3 分钟记录今天最容易失控的时间点和触发原因"],
        notice: "不要用极端节食补偿上一顿，也不要把偶尔的放松理解为失败。",
      },
      "15": {
        headline: "用 15 分钟搭好未来一天的饮食底盘",
        reason: "饮食不规律往往不是意志力不足，而是工作忙碌时缺少可直接选择的食物和固定进餐节点。一刻钟足够先搭一个简单框架。",
        actions: ["用 5 分钟规划明天三餐的大致时间，不要求精确计算", "用 5 分钟准备一份方便携带的简单食物或加餐", "用 5 分钟整理外卖备选，保留几个相对稳定、不过度油腻的选择"],
        notice: "如有特殊疾病、过敏、进食障碍或明确营养需求，请遵循医生或专业营养人员建议。",
      },
    },
  },
  MOOD: {
    code: "MOOD",
    name: "情绪减负",
    variants: {
      "5": {
        headline: "先把今天最耗你的那件事，缩小一格",
        reason: "任务模糊、边界不清和持续等待，容易让情绪在后台反复运行。先把问题写清楚，可以减少一部分无意义的心理占用。",
        actions: ["写下今天唯一必须完成的一件事", "用一句话明确它的完成标准和截止时间", "下班前写下下一步动作，让大脑暂时停止反复提醒"],
        notice: "持续或严重的情绪困扰，应向可信赖的人或专业人员寻求帮助。",
      },
      "10": {
        headline: "用 10 分钟清掉一轮情绪后台",
        reason: "内耗通常不是事情太多，而是大量未明确、未关闭的问题同时占用注意力。短暂整理可以重新获得掌控感。",
        actions: ["用 3 分钟写下当前最困扰你的事情，不评价对错", "用 4 分钟把事情分成“现在能做”“需要确认”“暂时放下”三类", "用 3 分钟只完成一个最小动作，例如发出确认消息或建立待办"],
        notice: "不需要一次解决所有问题，本轮目标只是减少一个持续占用注意力的事项。",
      },
      "15": {
        headline: "用 15 分钟做一次完整的情绪清仓",
        reason: "长期烦躁和内耗，往往来自任务堆积、沟通边界不清以及工作结束后仍持续思考。一刻钟可以先完成一次集中整理和收尾。",
        actions: ["用 5 分钟写下待办和担忧，把它们从脑中移到纸面", "用 5 分钟区分必须处理、可以协商和无需承担的事情", "用 5 分钟完成下班收尾，停止继续处理工作消息"],
        notice: "如果情绪持续低落、明显影响工作生活，或出现无法控制的焦虑，请尽快寻求专业支持。",
      },
    },
  },
};
